<template>
    <div>
        <div class="section-divider">
            <span class="section-title">Activity Feed</span>
        </div>

        <!-- Type filter -->
        <div class="analytics-tabs mb-3" v-if="!loading && activities.length > 0">
            <button
                class="tab-btn"
                :class="{ active: typeFilter === null }"
                @click="filterByType(null)"
            >All</button>
            <button
                v-for="t in activityTypes"
                :key="t"
                class="tab-btn"
                :class="{ active: typeFilter === t }"
                @click="filterByType(t)"
            >{{ capitalize(t) }}</button>
        </div>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-2">Loading activity...</p>
        </div>

        <div v-else-if="error" class="compass-info-box compass-info-box--danger">
            <p class="mb-0">{{ error }}</p>
        </div>

        <div v-else-if="activities.length === 0" class="compass-info-box">
            <p class="mb-0">No activity found for this wallet.</p>
        </div>

        <div v-else>
            <div class="live-feed">
                <div
                    v-for="a in activities"
                    :key="a.signature"
                    class="feed-item"
                >
                    <div :class="['feed-icon', feedIconClass(a.type)]">
                        {{ feedIconEmoji(a.type) }}
                    </div>
                    <div class="feed-content">
                        <div class="feed-title">
                            <span class="compass-badge compass-badge--outline me-2" style="font-size: 0.7rem;">
                                {{ a.action || a.type || 'action' }}
                            </span>
                            {{ a.summary || buildSummary(a) }}
                        </div>
                        <div class="feed-meta">
                            <span v-if="a.program_name">{{ a.program_name }}</span>
                            <span v-else-if="a.program_id" class="font-mono">{{ formatPubkey(a.program_id) }}</span>
                            <span class="mx-1">&middot;</span>
                            <a
                                :href="`https://solscan.io/tx/${a.signature}`"
                                target="_blank"
                                class="font-mono text-muted"
                            >{{ formatPubkey(a.signature, 6, 4) }}</a>
                        </div>
                    </div>
                    <div class="feed-value" v-if="a.primary_token && a.primary_token.amount_usd">
                        <div class="value mono-num">${{ formatValue(a.primary_token.amount_usd) }}</div>
                    </div>
                    <div class="feed-time">{{ formatTimeAgo(a.timestamp) }}</div>
                </div>
            </div>

            <!-- Load more -->
            <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.has_more || pagination.offset > 0">
                <button
                    class="compass-btn compass-btn--sm"
                    :disabled="pagination.offset === 0"
                    @click="loadPage(pagination.offset - pagination.limit)"
                >Previous</button>
                <span class="text-muted small">
                    Showing {{ pagination.offset + 1 }}-{{ Math.min(pagination.offset + activities.length, pagination.total) }}
                    of {{ pagination.total }}
                </span>
                <button
                    class="compass-btn compass-btn--sm"
                    :disabled="!pagination.has_more"
                    @click="loadPage(pagination.offset + pagination.limit)"
                >Next</button>
            </div>
        </div>
    </div>
</template>

<script>
import { formatPubkey } from '../composables/useFormatters';

export default {
    name: 'WalletActivity',
    props: {
        address: { type: String, required: true },
    },
    emits: ['loaded'],
    data() {
        return {
            activities: [],
            pagination: { offset: 0, limit: 50, total: 0, has_more: false },
            loading: true,
            error: null,
            typeFilter: null,
            activityTypes: [],
        };
    },
    methods: {
        formatPubkey,
        capitalize(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        feedIconClass(type) {
            const map = {
                swap: 'feed-icon--swap',
                transfer: 'feed-icon--transfer',
                mint: 'feed-icon--mint',
                burn: 'feed-icon--mint',
                stake: 'feed-icon--transfer',
            };
            return map[type] || '';
        },
        feedIconEmoji(type) {
            const map = {
                swap: '\u21C4',
                transfer: '\u2192',
                mint: '\u2795',
                burn: '\u2796',
                stake: '\u26A1',
            };
            return map[type] || '\u25CF';
        },
        formatValue(val) {
            if (!val) return '0';
            if (Math.abs(val) >= 1e6) return (val / 1e6).toFixed(2) + 'M';
            if (Math.abs(val) >= 1e3) return (val / 1e3).toFixed(1) + 'K';
            return val.toFixed(2);
        },
        formatTimeAgo(timestamp) {
            if (!timestamp) return '';
            const diff = Date.now() - new Date(timestamp).getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 1) return 'just now';
            if (mins < 60) return mins + 'm ago';
            const hours = Math.floor(mins / 60);
            if (hours < 24) return hours + 'h ago';
            const days = Math.floor(hours / 24);
            if (days < 30) return days + 'd ago';
            return new Date(timestamp).toLocaleDateString();
        },
        buildSummary(a) {
            const parts = [];
            if (a.primary_token) {
                const sym = a.primary_token.symbol || 'token';
                parts.push(sym);
            }
            if (a.secondary_token) {
                const sym = a.secondary_token.symbol || 'token';
                parts.push(sym);
            }
            if (parts.length === 2) return `${parts[0]} \u2192 ${parts[1]}`;
            if (parts.length === 1) return parts[0];
            return '';
        },
        filterByType(type) {
            this.typeFilter = type;
            this.fetchActivity(0);
        },
        async fetchActivity(offset = 0) {
            this.loading = true;
            this.error = null;
            try {
                let url = `/api/v1/wallets/${this.address}/activity?limit=50&offset=${offset}`;
                if (this.typeFilter) url += `&type=${this.typeFilter}`;
                const resp = await fetch(url);
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const json = await resp.json();
                const data = json.data || json;
                this.activities = data.activities || [];
                this.pagination = data.pagination || { offset: 0, limit: 50, total: 0, has_more: false };

                // Collect unique types for filter buttons (only on first load)
                if (this.activityTypes.length === 0 && !this.typeFilter) {
                    const types = new Set();
                    this.activities.forEach(a => { if (a.type) types.add(a.type); });
                    this.activityTypes = [...types].sort();
                }

                this.$emit('loaded', { total: this.pagination.total });
            } catch (e) {
                this.error = 'Failed to load activity. ' + e.message;
            } finally {
                this.loading = false;
            }
        },
        loadPage(offset) {
            this.fetchActivity(Math.max(0, offset));
        },
    },
    mounted() {
        this.fetchActivity();
    },
};
</script>
